/**
 * The decisions the review screen makes, kept out of React.
 *
 * Mirrors the server's rules in `course/review.py` closely enough that the
 * Publish button can be enabled or disabled without a round trip — the
 * server re-checks on POST and is the authority.
 */

export type Level = 'good' | 'warn' | 'bad';
export type Signal = [Level, string, string];

export type AcceptedLine = {san: string[]; uci: string[]; tag: 'coach' | 'engine' | 'alt'};

export type ReviewBlock = {
  score: Level;
  hard: boolean;
  side_source: string | null;
  coach_move: string | null;
  goal: string;
  lines: AcceptedLine[];
  signals: Signal[];
  machine: Signal[];
  transcript?: string;
  /**
   * What the coach said BEFORE the position appeared — the set-up, where the
   * task is stated. Kept apart from the rest of the window because it is the
   * only part a hint may be written from; what was said once the board was up
   * contains the answer.
   */
  lead_in?: string;
  /** The line the coach gave out loud, replayed and verified on a real board. */
  coach_line?: string[];
  /** The task as the coach stated it, which can differ from the engine's. */
  spoken_goal?: string | null;
  /** Motifs the coach named out loud. */
  themes?: string[];
};

export type ReviewPage = {
  id: number;
  content_type: 'chess_position' | 'video';
  content: string;
  heading?: string | null;
  text?: string | null;
  config: {review?: ReviewBlock; hint?: string; view?: 'w' | 'b'; [k: string]: unknown};
};

export type ReviewFlag = {
  id: number;
  page_id: number | null;
  kind: string;
  note: string;
  status: 'open' | 'fixed' | 'dismissed';
  dismiss_reason: string;
};

export type Status = {
  level: Level | 'fixed' | 'dismissed' | 'unknown';
  hard: boolean;
  label: string;
};

const strip = (san: string) => san.replace(/[+#]/g, '').trim();

/** A dismissal without a reason is not a dismissal. */
const reasoned = (f: ReviewFlag) => f.status === 'dismissed' && f.dismiss_reason.trim().length > 0;
const stillOpen = (f: ReviewFlag) => f.status === 'open' || (f.status === 'dismissed' && !reasoned(f));

/** What the rail shows for one page. */
export function chapterStatus(page: ReviewPage, flags: ReviewFlag[]): Status {
  const mine = flags.filter(f => f.page_id === page.id);
  const review = page.config?.review;

  if (review?.hard) return {level: 'bad', hard: true, label: 'fails a machine check'};

  const open = mine.filter(stillOpen);
  const dismissed = mine.filter(reasoned);
  const fixed = mine.filter(f => f.status === 'fixed');

  if (page.content_type === 'video' && !review) {
    if (open.length) return {level: 'warn', hard: false, label: 'needs a look'};
    if (dismissed.length) return {level: 'dismissed', hard: false, label: `dismissed · ${dismissed[0].dismiss_reason}`};
    if (fixed.length) return {level: 'fixed', hard: false, label: 'fixed'};
    return {level: 'good', hard: false, label: 'video'};
  }

  if (!review) return {level: 'unknown', hard: false, label: 'built before review existed'};

  if (review.score === 'bad') return {level: 'bad', hard: false, label: 'check this one'};
  if (open.length) return {level: 'warn', hard: false, label: 'needs a look'};
  // A soft machine score is not a flag: the coach launched these puzzles in class.
  if (dismissed.length) return {level: 'dismissed', hard: false, label: `dismissed · ${dismissed[0].dismiss_reason}`};
  if (fixed.length) return {level: 'fixed', hard: false, label: 'fixed'};
  return {level: 'good', hard: false, label: 'proven'};
}

export type GateCheck = {label: string; ok: boolean; detail: string};

/** Client-side mirror of `review.publish_gate`. The server is the authority. */
export function publishGate(
  pages: ReviewPage[],
  flags: ReviewFlag[],
  opened: Set<number>,
): {ok: boolean; checks: GateCheck[]} {
  if (!pages.length) {
    return {ok: false, checks: [{label: 'Course has pages', ok: false, detail: 'There is nothing to publish.'}]};
  }
  const puzzles = pages.filter(p => p.content_type === 'chess_position');
  const hard = pages.filter(p => p.config?.review?.hard);
  const open = flags.filter(stillOpen);
  const unseen = puzzles.filter(p => !opened.has(p.id));
  const dismissed = flags.filter(reasoned);

  const checks: GateCheck[] = [
    {
      label: 'Machine checks pass on every puzzle',
      ok: !hard.length,
      detail: hard.length
        ? `Failing: ${hard.map(p => p.id).join(', ')} — cannot be dismissed`
        : `${puzzles.length} puzzle lines replayed, all legal, all end on the student's move`,
    },
    {
      label: 'No open flags',
      ok: !open.length,
      detail: open.length ? `Open on pages: ${open.map(f => f.page_id).join(', ')}` : 'All resolved, or dismissed with a reason',
    },
    {
      label: 'Every puzzle opened by a reviewer',
      ok: !unseen.length,
      detail: unseen.length
        ? `Not yet opened: ${unseen.map(p => p.id).join(', ')}. Green is not a reason to skip.`
        : `All ${puzzles.length} opened`,
    },
    {
      label: 'Dismissals, listed',
      ok: true,
      detail: dismissed.length ? dismissed.map(f => `page ${f.page_id}: "${f.dismiss_reason}"`).join('; ') : 'None',
    },
  ];
  return {ok: checks.every(c => c.ok), checks};
}

/** The next page after `currentId` that needs a look, wrapping. Null if none. */
export function nextFlagged(pages: ReviewPage[], flags: ReviewFlag[], currentId: number): number | null {
  const needs = (p: ReviewPage) => {
    const s = chapterStatus(p, flags);
    return s.level === 'bad' || s.level === 'warn';
  };
  const ids = pages.map(p => p.id);
  const start = ids.indexOf(currentId);
  const order = [...ids.slice(start + 1), ...ids.slice(0, Math.max(start, 0))];
  for (const id of order) {
    const p = pages.find(x => x.id === id)!;
    if (needs(p)) return id;
  }
  return null;
}

/**
 * The next page worth the reviewer's attention: a flagged one if any remain,
 * otherwise a puzzle they have not opened yet.
 *
 * Review used to run out of road. Once nothing was flagged, "Next flagged"
 * went dead — but the publish gate still demanded every puzzle be opened, and
 * the only way to satisfy it was to hunt the rail by hand against a list of
 * raw page ids. On a 27-puzzle course that is the whole job, unassisted.
 *
 * Videos are skipped when looking for something unopened, because the gate
 * does not count them either.
 */
export function nextToReview(
  pages: ReviewPage[],
  flags: ReviewFlag[],
  currentId: number,
  opened: Set<number>,
): {id: number; why: 'flagged' | 'unopened'} | null {
  const flagged = nextFlagged(pages, flags, currentId);
  if (flagged != null) return {id: flagged, why: 'flagged'};

  const ids = pages.map(p => p.id);
  const start = ids.indexOf(currentId);
  const order = [...ids.slice(start + 1), ...ids.slice(0, Math.max(start, 0))];
  for (const id of order) {
    const p = pages.find(x => x.id === id)!;
    if (p.content_type !== 'video' && !opened.has(id)) return {id, why: 'unopened'};
  }
  return null;
}

/** How a line the reviewer played compares with the accepted solutions. */
export function linesMatch(played: string[], accepted: AcceptedLine[]): 'complete' | 'prefix' | 'off' {
  const mine = played.map(strip);
  let prefix = false;
  for (const line of accepted) {
    const theirs = line.san.map(strip);
    if (mine.length === theirs.length && mine.every((m, i) => m === theirs[i])) return 'complete';
    if (mine.length < theirs.length && mine.every((m, i) => m === theirs[i])) prefix = true;
  }
  return prefix ? 'prefix' : 'off';
}

/**
 * The move the engine's choice is judged against: the primary accepted line's
 * next move after what the reviewer has played. Null once play leaves that
 * line or reaches its end — the engine is still shown, just not graded.
 */
export function nextSolutionMove(
  accepted: AcceptedLine[],
  played: string[],
): {san: string; uci: string | undefined} | null {
  const primary = accepted[0];
  if (!primary) return null;
  const at = played.length;
  if (at >= primary.san.length) return null;
  if (!played.every((m, i) => strip(m) === strip(primary.san[i]))) return null;
  return {san: primary.san[at], uci: primary.uci?.[at] || undefined};
}
